<template>
	<ViewFeedItem class="view-feed-contenttutoriallist">
		<template v-slot:header>
			<ViewLayoutModuleHeader v-bind:title="title" v-bind:subtitle="subtitle">
				<RouterLink v-bind:to="{ name: 'PageContent' }">
					<strong>View All</strong>
				</RouterLink>
			</ViewLayoutModuleHeader>
		</template>

		<template v-slot:body>
			<ViewUiStateLoader v-if="contentCollection.length === 0" />

			<ul class="rows" v-if="contentCollection.length > 0">
				<li
					class="row"
					v-bind:key="contentModel.id"
					v-for="contentModel in contentCollection.models"
					v-on:click="Handle_OnSelectContent(contentModel)"
				>
					<div class="thumbnail" v-if="contentModel.getThumbnailUrl()">
						<img v-bind:alt="contentModel.getTitle()" v-bind:src="contentModel.getThumbnailUrl()" />
					</div>

					<div class="meta">
						<h4 class="title">{{ contentModel.getTitle() }}</h4>
						<p class="date">{{ formatDate(contentModel) }}</p>
					</div>
				</li>
			</ul>
		</template>
	</ViewFeedItem>
</template>

<script lang="ts">
	import ChalkySticks from '@chalkysticks/sdk';
	import ViewBase from '@/Core/View.vue';
	import { Component, Prop } from 'vue-property-decorator';
	import { mounted } from '@/Utility/Decorators';

	/**
	 * Compact vertical list of tutorial articles. Pairs a small thumbnail
	 * with title and date &mdash; designed to fit a sidebar-style slot in the
	 * home feed without dominating the layout.
	 *
	 * @author Matt Kenefick <matt@chalkysticks.com>
	 * @package View/Feed
	 * @project ChalkySticks App
	 */
	@Component
	export default class ViewFeedContentTutorialList extends ViewBase {
		/**
		 * Subtitle copy beneath the module header
		 *
		 * @return string
		 */
		protected get subtitle(): string {
			return ''; // 'Sharpen your game';
		}

		/**
		 * Title copy in the module header
		 *
		 * @return string
		 */
		protected get title(): string {
			return 'Tutorials';
		}

		/**
		 * Source collection of tutorial content
		 *
		 * @type ChalkySticks.Collection.Content
		 */
		@Prop({
			default: () => ChalkySticks.Factory.Content.collection({ limit: 5 }).tutorials(),
		})
		public contentCollection!: ChalkySticks.Collection.Content;

		/**
		 * Fetch tutorials on mount.
		 *
		 * @return Promise<void>
		 */
		@mounted
		public async setup(): Promise<void> {
			await this.contentCollection.fetch();
		}

		/**
		 * Returns relative time for recent content, formatted date for older items
		 *
		 * @param ChalkySticks.Model.Content contentModel
		 * @return string
		 */
		protected formatDate(contentModel: ChalkySticks.Model.Content): string {
			if (contentModel.isMonthOld('published_at')) {
				return contentModel.getPublishedAt('MMM D, YYYY');
			}

			return contentModel.getPublishedAtTimeAgo();
		}

		// region: Event Handlers
		// ---------------------------------------------------------------------------

		/**
		 * @param ChalkySticks.Model.Content contentModel
		 * @return Promise<void>
		 */
		protected async Handle_OnSelectContent(contentModel: ChalkySticks.Model.Content): Promise<void> {
			this.$router.push({
				name: 'PageContentItem',
				params: {
					id: contentModel.getSlug(),
				},
			});
		}

		// endregion: Event Handlers
	}
</script>

<style lang="scss">
	// Layout
	// ---------------------------------------------------------------------------

	.view-feed-contenttutoriallist {
		.body {
			padding: 0 var(--padding) var(--padding);
		}

		.rows {
			display: flex;
			flex-direction: column;
			gap: 0.5rem;
			list-style: none;
			margin: 0;
			padding: 0;
		}

		.row {
			align-items: center;
			background: var(--chalky-blue-4);
			border-radius: 0.5rem;
			cursor: pointer;
			display: flex;
			gap: 0.75rem;
			overflow: hidden;
			padding: 0.5rem;
			transition: background 0.2s ease-in-out;
		}

		.thumbnail {
			aspect-ratio: 16 / 9;
			background: var(--chalky-blue-3);
			border-radius: 0.25rem;
			flex: 0 0 auto;
			overflow: hidden;
			width: 6rem;

			img {
				height: 100%;
				object-fit: cover;
				width: 100%;
			}
		}

		.meta {
			display: flex;
			flex: 1;
			flex-direction: column;
			gap: 0.25rem;
			min-width: 0;

			.title {
				display: -webkit-box;
				font-size: 0.95rem;
				font-weight: var(--font-weight-semibold);
				line-height: 1.3;
				overflow: hidden;
				-webkit-box-orient: vertical;
				-webkit-line-clamp: 2;
			}

			.date {
				color: var(--chalky-grey-2);
				font-size: 0.75rem;
			}
		}
	}

	// User Interaction
	// ---------------------------------------------------------------------------

	.view-feed-contenttutoriallist .row:active {
		background: var(--chalky-blue-3);
	}
</style>
